import { useState, useEffect, useCallback } from 'react';
import { Loader2, Star, FileText, Download } from 'lucide-react';
import { api, type LlamadasGuardadas, type LlamadaDetalle } from '@/lib/api';
import Modal from '@/components/ui/Modal';
import { toCsv } from '@/lib/utils';
import { formatDuracion } from './LlamadasMetricasPage';
import { enMadrid, SENTIMIENTO, ContenidoLlamada, TarjetaLlamada, BotonGuardar } from './LlamadasListaPage';
import BotonLlamar from '@/components/centralita/BotonLlamar';
import CampoEditable from '@/components/llamadas/CampoEditable';
import ProcesarLead from '@/components/llamadas/ProcesarLead';

/**
 * Las llamadas marcadas para guardar.
 *
 * No depende del periodo elegido arriba: se guarda una llamada precisamente
 * para volver a ella más tarde, y hacerla desaparecer porque el selector está
 * en «30 días» rompería justamente eso. Por eso esta pestaña ignora el rango y
 * las muestra todas, de la marcada más recientemente a la más antigua.
 */
export default function LlamadasGuardadasPage() {
  const [data, setData] = useState<LlamadasGuardadas | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<LlamadaDetalle | null>(null);
  const [procesandoLead, setProcesandoLead] = useState(false);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getLlamadasGuardadas());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando las llamadas guardadas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  /** Al desmarcar, la llamada sale de la lista: es la lista de lo marcado. */
  const quitar = async (id: string) => {
    const antes = data;
    setData((d) => (d ? { total: d.total - 1, items: d.items.filter((i) => i.id !== id) } : d));
    setDetalle((d) => (d && d.id === id ? { ...d, guardada: false } : d));
    try {
      await api.marcarLlamada(id, false);
    } catch (e) {
      setData(antes);
      setError(e instanceof Error ? e.message : 'No se pudo quitar de guardadas');
    }
  };

  /** Corregir desde aquí también: se revisa una llamada guardada tanto como una del listado. */
  const corregir = async (id: string, campos: { guestName?: string; listingNickname?: string }) => {
    try {
      const r = await api.corregirLlamada(id, campos);
      const parche = (i: any) => {
        if (i.id !== id) return i;
        const corr = { ...(i.corregido ?? { guestName: false, listingNickname: false }) };
        if (campos.guestName !== undefined) corr.guestName = true;
        if (campos.listingNickname !== undefined) corr.listingNickname = true;
        return {
          ...i,
          ...(campos.guestName !== undefined ? { guestName: r.guestName } : {}),
          ...(campos.listingNickname !== undefined ? { listingNickname: r.listingNickname } : {}),
          corregido: corr,
        };
      };
      setData((d) => (d ? { ...d, items: d.items.map(parche) } : d));
      setDetalle((d) => (d && d.id === id ? parche(d) : d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la corrección');
    }
  };

  const restaurar = async (id: string) => {
    try {
      await api.quitarCorreccionLlamada(id);
      const fresca = await api.getLlamada(id);
      setData((d) => (d ? { ...d, items: d.items.map((i) => (i.id === id ? { ...i, ...fresca } : i)) } : d));
      setDetalle((d) => (d && d.id === id ? fresca : d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo deshacer la corrección');
    }
  };

  const abrirDetalle = async (id: string) => {
    setCargandoDetalle(true);
    try {
      setDetalle(await api.getLlamada(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la llamada');
    } finally {
      setCargandoDetalle(false);
    }
  };

  const exportar = () => {
    const csv = toCsv([
      ['Guardada el', 'Fecha', 'Teléfono', 'Huésped', 'Alojamiento', 'Duración (s)', 'Motivo'],
      ...(data?.items ?? []).map((f) => [
        enMadrid(f.guardadaEn), enMadrid(f.startedAt), f.phone ?? '', f.guestName ?? '',
        f.listingNickname ?? '', f.durationSec ?? '', f.categoria ?? '',
      ]),
    ]);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'llamadas-guardadas.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      {error && <div className="card py-3 text-sm text-amber-700 dark:text-amber-300">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : !items.length ? (
        <div className="card text-center py-12 px-4">
          <Star className="mx-auto text-slate-300 dark:text-slate-600 mb-3" size={32} />
          <p className="text-slate-500">Todavía no has guardado ninguna llamada.</p>
          <p className="text-slate-400 text-sm mt-1">
            Pulsa la estrella junto a una llamada para tenerla aquí a mano.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              {items.length} {items.length === 1 ? 'llamada guardada' : 'llamadas guardadas'}
            </p>
            <button onClick={exportar} className="btn-secondary flex items-center gap-1.5 min-h-10 px-3">
              <Download size={15} /> CSV
            </button>
          </div>

          {/* Móvil: tarjetas, igual que en el listado. */}
          <div className="sm:hidden space-y-2">
            {items.map((ll) => (
              <TarjetaLlamada
                key={ll.id}
                ll={{ ...ll, guardada: true }}
                onAbrir={() => abrirDetalle(ll.id)}
                onGuardar={() => quitar(ll.id)}
                onCorregir={(campos) => corregir(ll.id, campos)}
                onRestaurar={() => restaurar(ll.id)}
                pie={<p className="text-[11px] text-slate-400 mt-1.5">Guardada el {enMadrid(ll.guardadaEn)}</p>}
              />
            ))}
          </div>

          {/* Escritorio: tabla, con la columna de cuándo se guardó. */}
          <div className="hidden sm:block card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
                <tr>
                  <th className="w-10 px-2 py-2.5"></th>
                  <th className="text-left font-medium px-4 py-2.5">Fecha</th>
                  <th className="text-left font-medium px-4 py-2.5">Huésped</th>
                  <th className="text-left font-medium px-4 py-2.5 hidden md:table-cell">Alojamiento</th>
                  <th className="text-left font-medium px-4 py-2.5 hidden lg:table-cell">Motivo</th>
                  <th className="text-left font-medium px-4 py-2.5">Guardada</th>
                  <th className="text-right font-medium px-4 py-2.5">Duración</th>
                  <th className="w-12 px-2 py-2.5"></th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((ll) => (
                  <tr key={ll.id} className="border-t border-slate-100 dark:border-white/5">
                    <td className="px-2 py-1.5">
                      <BotonGuardar guardada onCambiar={() => quitar(ll.id)} tamano={15} />
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{enMadrid(ll.startedAt)}</td>
                    <td className="px-4 py-2.5">
                      <CampoEditable
                        confirmado={ll.guestName}
                        oido={ll.nombreDicho}
                        corregido={ll.corregido?.guestName}
                        ficha={ll.fichaHuesped}
                        etiqueta="Editar el huésped"
                        onGuardar={(v) => corregir(ll.id, { guestName: v })}
                        onRestaurar={() => restaurar(ll.id)}
                      />
                      {ll.phone && <span className="text-slate-400 block text-xs">{ll.phone}</span>}
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <CampoEditable
                        confirmado={ll.listingNickname}
                        oido={ll.alojamientoDicho}
                        corregido={ll.corregido?.listingNickname}
                        vacio="—"
                        etiqueta="Editar el alojamiento"
                        onGuardar={(v) => corregir(ll.id, { listingNickname: v })}
                        onRestaurar={() => restaurar(ll.id)}
                      />
                    </td>
                    <td className="px-4 py-2.5 hidden lg:table-cell">
                      {ll.categoria ? <span className="badge text-[11px]">{ll.categoria.replace(/_/g, ' ')}</span> : <span className="text-slate-400">—</span>}
                      {ll.sentimientoGlobal && (
                        <span className={`ml-1.5 text-[11px] ${SENTIMIENTO[ll.sentimientoGlobal].etiqueta}`}>
                          {SENTIMIENTO[ll.sentimientoGlobal].cara}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-slate-500 text-xs">{enMadrid(ll.guardadaEn)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">{formatDuracion(ll.durationSec)}</td>
                    <td className="px-2 py-1.5">
                      <BotonLlamar numero={ll.phone} nombre={ll.guestName} alojamiento={ll.listingNickname} compacto />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => abrirDetalle(ll.id)} className="btn-ghost text-xs flex items-center gap-1 ml-auto">
                        <FileText size={13} /> Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal isOpen={Boolean(detalle) || cargandoDetalle} onClose={() => setDetalle(null)} title="Llamada" size="2xl">
        {cargandoDetalle || !detalle ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
        ) : (
          <ContenidoLlamada
            detalle={detalle}
            onGuardar={() => quitar(detalle.id)}
            onCorregir={(campos) => corregir(detalle.id, campos)}
            onRestaurar={() => restaurar(detalle.id)}
            onProcesarLead={() => setProcesandoLead(true)}
          />
        )}
      </Modal>

      {procesandoLead && detalle && (
        <ProcesarLead
          llamada={detalle}
          onCerrar={() => setProcesandoLead(false)}
          onProcesada={(r) => setDetalle((d) => (d ? { ...d, lead: r.enlace } : d))}
        />
      )}
    </div>
  );
}
